/* oxlint-disable typescript/consistent-return -- LINTER migration follow up */
import { parseKeyList } from "@grafit/shared/crypto/parse-key-list";
import { verifySignedRolesWithKeyList } from "@grafit/shared/crypto/signed-roles";
import {
  FALLBACK_USER_ROLES,
  type GrafitUserWithRoles,
  parseUserRoles,
  type UserRole,
} from "@grafit/shared/domain/user/user";
import { alertDevs } from "@grafit/shared/observability/sentry-front";
import { mapAuthUserToGrafitUser } from "@grafit/shared/services/auth";
import { createContext, use, useEffect, useMemo, useReducer, useState } from "react";
import { authClient } from "./auth-client.ts";
import { pinSessionId } from "./pinned-session-id.ts";
import { isSignedRoleCryptoSupported } from "./utils/is-signed-role-crypto-supported.ts";

const SESSION_PUBLIC_KEYS = (() => {
  const raw = import.meta.env["VITE_SESSION_PUBLIC_KEYS"] as string | undefined;
  if (!raw) {
    return undefined;
  }
  try {
    return parseKeyList(raw);
  } catch {
    return undefined;
  }
})();

interface AuthCtx {
  status: "guest" | "authed" | "pending";
  user: GrafitUserWithRoles | null;
  roles: UserRole[];
  isSignedRoleCryptoSupported: boolean | null;
}

const AuthContext = createContext<AuthCtx | null>(null);
AuthContext.displayName = "AuthContext";

interface VerificationState {
  verifiedRoles: UserRole[] | null;
  isVerifying: boolean;
  prevData: ReturnType<(typeof authClient)["useSession"]>["data"];
}

type VerificationAction =
  | { type: "reset" }
  | { type: "start_verifying" }
  | { type: "verified"; roles: UserRole[]; prevData: VerificationState["prevData"] }
  | { type: "fallback"; roles: UserRole[] };

function verificationReducer(state: VerificationState, action: VerificationAction): VerificationState {
  switch (action.type) {
    case "reset":
      return { verifiedRoles: null, isVerifying: false, prevData: state.prevData };
    // to avoid flickering, we keep the old roles while verifying
    case "start_verifying":
      return { verifiedRoles: state.verifiedRoles, isVerifying: true, prevData: state.prevData };
    case "verified":
      return { verifiedRoles: action.roles, isVerifying: false, prevData: action.prevData };
    case "fallback":
      return { verifiedRoles: action.roles, isVerifying: false, prevData: state.prevData };
  }
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const { data, isPending } = authClient.useSession();
  const [isCryptoSupported, setIsCryptoSupported] = useState<boolean | null>(null);

  const [{ verifiedRoles, prevData, isVerifying }, dispatch] = useReducer(verificationReducer, {
    verifiedRoles: null,
    isVerifying: false,
    prevData: null,
  });

  useEffect(() => {
    let ignore = false;
    void isSignedRoleCryptoSupported().then((isSupported) => {
      if (!ignore) {
        setIsCryptoSupported(isSupported);
      }
      return undefined;
    });
    return () => {
      ignore = true;
    };
  }, []);

  useEffect(() => {
    let ignore = false;

    if (!data) {
      dispatch({ type: "reset" });
      return;
    }

    pinSessionId(data.session.id);

    if (isCryptoSupported === null) {
      return;
    }

    if (!isCryptoSupported) {
      dispatch({ type: "fallback", roles: FALLBACK_USER_ROLES });
      return;
    }

    if (!data.signedRoles || !SESSION_PUBLIC_KEYS) {
      alertDevs({
        error: new Error("Frontend: missing signed roles or public keys"),
        context: { userId: data.user.id, hasSignedRoles: !!data.signedRoles, hasPublicKeys: !!SESSION_PUBLIC_KEYS },
      });
      dispatch({ type: "fallback", roles: FALLBACK_USER_ROLES });
      return;
    }

    dispatch({ type: "start_verifying" });
    verifySignedRolesWithKeyList({
      signedRoles: data.signedRoles,
      expectedUserId: data.user.id,
      publicKeyPems: SESSION_PUBLIC_KEYS,
    })
      .then((verified) => {
        if (!ignore) {
          dispatch({ type: "verified", roles: parseUserRoles(verified.roles), prevData: data });
        }
        return undefined;
      })
      .catch((caughtError: unknown) => {
        if (!ignore) {
          alertDevs({
            error: new Error("Frontend: failed to verify signed roles"),
            exception: caughtError,
            context: { userId: data.user.id },
          });
          dispatch({ type: "fallback", roles: FALLBACK_USER_ROLES });
        }
      });

    return () => {
      ignore = true;
    };
  }, [data, isCryptoSupported]);

  const roles = verifiedRoles ?? FALLBACK_USER_ROLES;

  const value = useMemo((): AuthCtx => {
    // biome-ignore lint/complexity/noExcessiveCognitiveComplexity: it is readable
    const calculateStatus = () => {
      if (isPending || isVerifying || (data && isCryptoSupported === null)) {
        return prevData ? "authed" : "pending";
      }
      return data ? "authed" : "guest";
    };

    const userData = isPending || isVerifying ? prevData : data;

    return {
      status: calculateStatus(),
      user: userData
        ? mapAuthUserToGrafitUser({
            user: userData.user,
            roles,
            data: {
              phoneNumber: userData.phoneNumber,
              jobTitle: userData.jobTitle,
              trigram: userData.trigram,
            },
          })
        : null,
      roles,
      isSignedRoleCryptoSupported: isCryptoSupported,
    };
  }, [data, isCryptoSupported, isPending, isVerifying, roles, prevData]);

  return <AuthContext value={value}>{children}</AuthContext>;
}

export function useAuth() {
  const ctx = use(AuthContext);
  if (!ctx) {
    throw new Error("AuthProvider missing");
  }
  return ctx;
}
export function SignedOut({ children }: { children: React.ReactNode }) {
  const auth = useAuth();
  return auth.status === "guest" ? children : null;
}
export function IsPendingAuth({ children }: { children: React.ReactNode }) {
  const auth = useAuth();
  return auth.status === "pending" ? children : null;
}
export function SignedIn({ children }: { children: React.ReactNode }) {
  const auth = useAuth();
  return auth.status === "authed" ? children : null;
}
